import { useOutletContext } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePayers } from 'lib/hooks/queries/payers/usePayers';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import EmptyDataRows from 'components/shared/AgTablePage/components/EmptyDataRows';
import InfoBanner from 'components/shared/OnboardingWizard/components/InfoBanner/InfoBanner';
import SubstepConfirmationModal from 'components/shared/OnboardingWizard/components/SubstepConfirmationModal/SubstepConfirmationModal';

import PayerModal from './components/PayerModal';
import MotionActions from './components/MotionActions';
import { defaultColumns, getDefaultFilters } from './lib/helper';
import AddPayerModal from './components/AddPayerModal/AddPayerModal';

const OnboardingPayers = ({
  setHeaderButtonProps,
  addFooterActions,
  onNext,
  setHeaderInfoTooltip,
  mutateOrchestratorData,
  orchestratorLoading
} = {}) => {
  const { practiceId } = useOutletContext() || {};
  const [selectedPayer, setSelectedPayer] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const columns = useMemo(() => defaultColumns(practiceId), [practiceId]);
  const filters = useMemo(() => getDefaultFilters(practiceId), [practiceId]);
  const queryParams = useMemo(() => ({ practiceId, includePending: true }), [practiceId]);

  const { data: payersData } = usePayers({
    params: { practiceId, includePending: true },
    dependencies: [practiceId]
  });

  const hasPayers = (payersData?.payers ?? [])?.length > 0;

  useEffect(() => {
    addFooterActions([
      {
        text: 'Continue',
        onClick: async () => {
          if (!hasPayers) {
            setShowConfirmModal(true);
            return;
          }
          mutateOrchestratorData();
        },
        isDisabled: orchestratorLoading
      }
    ]);
  }, [orchestratorLoading, hasPayers]);

  const handleCellClick = useCallback((event) => {
    const target = event?.event?.target;
    if (target?.closest?.('[data-payer-info-icon]')) {
      return;
    }

    if (event?.data) {
      setSelectedPayer(event.data);
    }
  }, []);

  const handleClosePayerModal = useCallback(() => {
    setSelectedPayer(null);
  }, []);

  const renderEmptyState = useCallback(
    () => (
      <EmptyDataRows
        title="No payers added yet"
        description="Press 'New payer' then select a payer to add it to your list."
        emptyTextButton="New payer"
      />
    ),
    [practiceId]
  );

  const renderActions = useCallback(
    (props) => <MotionActions {...props} setMotionHeaderButtonProps={setHeaderButtonProps} />,
    [setHeaderButtonProps]
  );

  const renderModalComponent = useCallback(
    (props) => <AddPayerModal {...props} practiceId={practiceId} />,
    [practiceId]
  );

  return (
    <div className="mb-4 flex flex-1 flex-col gap-5">
      <InfoBanner
        title="Priority notice"
        description="To maximize your time with our trainers, complete every item before its corresponding live training. Arriving prepared ensures hands-on practice and fewer follow-ups."
        onDismiss={setHeaderInfoTooltip}
      />
      <AgTablePage
        name="payers"
        defaultColumns={columns}
        defaultFilters={filters}
        noRowsOverlayComponent={renderEmptyState}
        queryParams={queryParams}
        useData={usePayers}
        actions={renderActions}
        title="Payer"
        noHeader
        rowStyle={{ cursor: 'pointer' }}
        ModalComponent={renderModalComponent}
        onCellClicked={handleCellClick}
        className="overflow-y-auto rounded-2xl border border-solid border-neutral-200"
        autoHeight
      />

      {selectedPayer && (
        <PayerModal
          isOpen
          payerId={selectedPayer?.id}
          practiceId={practiceId}
          handleClose={handleClosePayerModal}
        />
      )}

      <SubstepConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onMarkComplete={() => {
          setShowConfirmModal(false);
          mutateOrchestratorData({ skipPayers: true });
        }}
        onSkip={() => {
          setShowConfirmModal(false);
          onNext();
        }}
        title="No payers added"
        message="You haven't added any payers yet. Choose how you'd like to proceed before moving on."
        isLoading={orchestratorLoading}
      />
    </div>
  );
};

export default withErrorBoundary(OnboardingPayers);
